/**
 * Whether a locale is safe to remove.
 *
 * Removing a locale that still has content would orphan that content: the
 * translations stay in the entities but no longer appear in any switcher, so
 * they are invisible and unrecoverable through the UI. Every caller must
 * check first, and the check has to name what is in the way -- "cannot
 * delete" with no reason is the kind of error people work around by deleting
 * the content instead.
 *
 * Shared because articles and the service map store locales differently
 * (a string list vs. a Language entity) but hold translations in the same
 * shape: `translations: Record<locale, unknown>` on each entity.
 */

import { hasTranslationContent } from "./languages"

export interface LocaleUsage {
  /** Number of entities carrying a translation in this locale. */
  count: number
  /** A few names, for an error the user can act on. */
  examples: string[]
  /** True when the locale is the store's fallback and so cannot be removed. */
  isDefault: boolean
}

const MAX_EXAMPLES = 3

/**
 * Counts entities holding a translation in `locale`.
 *
 * `entities` is every translatable record in the store; `nameOf` supplies a
 * human label for the examples. A translation counts as present only if the
 * key exists AND holds something -- an empty object left behind by a deleted
 * translation should not pin a locale in place forever.
 */
export function localeUsage(
  entities: Iterable<{ translations?: Record<string, unknown> | null }>,
  locale: string,
  nameOf: (entity: any) => string,
  defaultLocale: string,
): LocaleUsage {
  let count = 0
  const examples: string[] = []

  for (const e of entities) {
    const t = e.translations?.[locale]
    if (!t) continue
    if (typeof t === "object" && !hasTranslationContent(t)) continue
    count++
    if (examples.length < MAX_EXAMPLES) examples.push(nameOf(e))
  }

  return { count, examples, isDefault: locale === defaultLocale }
}

/** Human-readable reason a locale cannot be removed, or null when it can. */
export function localeRemovalBlocker(usage: LocaleUsage, locale: string): string | null {
  if (usage.isDefault) {
    return `${locale} is the default locale. Change the default before removing it.`
  }
  if (usage.count === 0) return null

  const shown = usage.examples.join(", ")
  const more = usage.count - usage.examples.length
  const tail = more > 0 ? `${shown}, and ${more} more` : shown
  return usage.count === 1
    ? `${locale} still has a translation on ${tail}. Remove it first.`
    : `${locale} still has translations on ${usage.count} records (${tail}). Remove them first.`
}
